import { t } from "@lingui/core/macro";
import { lazy, Suspense, useEffect, useState } from "react";
import Sidebar from "../../Components/Sidebar/Sidebar";
import useAccountInfo from "../../hooks/useAccountInfo";
import usePreferredLanguageStore from "../../stores/usePreferredLanguageStore";
import { ScrapeAcceptanceModal } from "@/Components/ScrapeAcceptanceModal";
import { api } from "@/lib/api-client";
import { usePosPrinterStore } from "@/stores/pos-printer-store";
import RouteChangeListener from "./RouteChangeListener";
import { DashboardRoutes } from "./DashboardRoutes";
import { AiChatProvider } from "@/features/ai-chat/AiChatProvider";
import { useHasAiChatAccess } from "@/features/ai-chat/useHasAiChatAccess";
import { useClientFeature } from "@/hooks/useClientFeature";
import { addToast, Chip } from "@heroui/react";
import { HighlightProvider } from "@/providers/HighlightProvider";
import { MfaSetupModal } from "@/Components/MfaSetup/MfaSetupModal";
import { UnpaidInvoiceBanner } from "@/Components/UnpaidInvoiceBanner/UnpaidInvoiceBanner";
import { AnchorReviewBanner } from "@/Components/AnchorReviewBanner/AnchorReviewBanner";
import { AnnouncementHost } from "@/features/announcements/AnnouncementHost";
import { TapToPayAwarenessModal } from "@/Components/TapToPayAwareness/TapToPayAwarenessModal";
import { useTapToPayAwareness } from "@/Components/TapToPayAwareness/useTapToPayAwareness";
import { useTapToPayWarmUp } from "@/pages/Dashboard/Integrations/PaymentTerminals/useTapToPayWarmUp";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { flushPendingSignupTracking } from "@/utils/signupTracking";

const AiChatPanel = lazy(() =>
  import("@/features/ai-chat/AiChatPanel").then((module) => ({
    default: module.AiChatPanel,
  })),
);
const SelfOnboardingWizard = lazy(() =>
  import("@/features/self-onboarding/SelfOnboardingWizard").then((module) => ({
    default: module.SelfOnboardingWizard,
  })),
);

const getCertificateSettingNames = (version: string) => ({
  cert:
    version === "v1" ? "qz_tray_client_crt" : "qz_tray_client_crt_2025_10_01",
  key:
    version === "v1" ? "qz_tray_client_key" : "qz_tray_client_key_2025_10_01",
});

const Dashboard = () => {
  const [isOnboardingSkipped, setIsOnboardingSkipped] = useState(false);
  const [isMfaSetupDismissed, setIsMfaSetupDismissed] = useState(false);

  const accountInfo = useAccountInfo((state) => state.accountInfo);
  const hasAiChatAccess = useHasAiChatAccess();

  const isAdminUser = ["admin", "salesman"].includes(accountInfo.systemRole);

  const { data: mfaStatus, error: mfaError } =
    api.mfa.useMfaStatus(isAdminUser);

  const mfaGraceExpired =
    mfaStatus?.gracePeriodRemainingDays != null &&
    mfaStatus.gracePeriodRemainingDays === 0;

  // A passkey is a faster sign-in, not a second factor on the password path, so
  // it does not satisfy the MFA requirement and must not silence the prompt.
  const shouldShowMfaSetupFromStatus =
    mfaStatus?.mfaSystemEnabled === true &&
    !mfaStatus.mfaEnabled &&
    (!isMfaSetupDismissed || mfaGraceExpired);

  const showMfaSetup =
    isAdminUser &&
    (shouldShowMfaSetupFromStatus ||
      (mfaStatus == null && mfaError != null && !isMfaSetupDismissed));
  // The MFA prompt preempts the announcement, so the announcement waits until it is known whether that prompt shows.
  const isMfaStatusPending =
    isAdminUser && mfaStatus == null && mfaError == null;

  const { isFeatureEnabled } = useClientFeature();

  const tapToPayAwareness = useTapToPayAwareness();
  const canManageIntegrations: boolean = useUserPermissionsStore(
    (state: { permissions: Record<string, boolean | undefined> }) =>
      state.permissions.view_integrations_page === true,
  );
  const { mutateAsync: getTapToPaySession } =
    api.paymentTerminals.usePostTapToPaySession();
  useTapToPayWarmUp({
    enabled: isFeatureEnabled("paymentTerminals") && canManageIntegrations,
    getSession: async () => (await getTapToPaySession()).session,
  });

  const { data: globalSettings } = api.app.useGetGlobalSettings(
    !isFeatureEnabled("taxRegister"),
  );

  const { loadPreferredLanguage } = usePreferredLanguageStore((state) => state);
  useEffect(() => {
    loadPreferredLanguage();
  }, []);

  // Registration reloads the page straight into the dashboard; fire the
  // pending sign_up_completed GTM event now that GTM is loaded again.
  useEffect(() => {
    flushPendingSignupTracking();
  }, []);

  useEffect(() => {
    if (!isAdminUser || !mfaError || mfaStatus != null || isMfaSetupDismissed) {
      return;
    }

    addToast({
      title: t`MFA status unavailable`,
      description: t`We could not verify your MFA status, so setup is being shown until the check succeeds.`,
      color: "warning",
    });
  }, [isAdminUser, isMfaSetupDismissed, mfaError, mfaStatus]);

  const { setupQzTray } = usePosPrinterStore((state) => state);
  useEffect(() => {
    if (!globalSettings || !accountInfo) return;

    const qzTrayClientCert = globalSettings.find(
      (setting) =>
        setting.settingName ===
        getCertificateSettingNames(accountInfo.qzTrayCertVersion).cert,
    );
    const qzTrayClientKey = globalSettings.find(
      (setting) =>
        setting.settingName ===
        getCertificateSettingNames(accountInfo.qzTrayCertVersion).key,
    );

    if (
      !qzTrayClientCert ||
      !qzTrayClientKey ||
      !qzTrayClientCert.settingValue ||
      !qzTrayClientKey.settingValue
    ) {
      // addToast({
      //   title: t`Povezave s POS tiskalnikom ni bilo mozno vzpostaviti.`,
      //   color: "danger",
      // });
      return;
    }

    setupQzTray({
      qzTrayClientCert: atob(qzTrayClientCert.settingValue),
      qzTrayClientKey: atob(qzTrayClientKey.settingValue),
    });
  }, [globalSettings, accountInfo]);

  return (
    <>
      <RouteChangeListener />
      <HighlightProvider>
        {/* Desktop: pin the banner + shell into one viewport-tall flex column
            so the banner's height is taken out of the shell's scroll area
            rather than added on top of it (which spawned a second, page-level
            scrollbar next to the content's own scroll). Mobile keeps page
            scroll. The 100vh subtracts the safe-area top padding on body so the
            shell doesn't overflow the viewport inside the Capacitor app. */}
        <AiChatProvider>
          <div className="flex flex-col md:h-[calc(100vh_-_var(--safe-area-top,0px))] md:overflow-hidden">
            <UnpaidInvoiceBanner />
            <AnchorReviewBanner />
            <div className="flex min-h-[calc(100vh_-_var(--safe-area-top,0px))] bg-[#f2f7f5] md:min-h-0 md:flex-1 dark:bg-[#0e0e0e]">
              <Sidebar />

              {accountInfo.scrapeCompletedAt &&
                !accountInfo.scrapeAcceptedAt && <ScrapeAcceptanceModal />}

              {!accountInfo.onboardingDone && !isOnboardingSkipped && (
                // Covers the calendar while the wizard chunk loads, so it does not flash.
                <Suspense
                  fallback={
                    <div className="fixed inset-0 z-50 bg-background" />
                  }
                >
                  <SelfOnboardingWizard
                    onSkip={() => setIsOnboardingSkipped(true)}
                  />
                </Suspense>
              )}

              <FiscalizationEnvironmentFloater />

              {/* Never stacked on the onboarding, scrape or MFA modals above. */}
              {Boolean(accountInfo.onboardingDone) &&
                !(
                  accountInfo.scrapeCompletedAt && !accountInfo.scrapeAcceptedAt
                ) &&
                !showMfaSetup &&
                (tapToPayAwareness.shouldShow ? (
                  <TapToPayAwarenessModal
                    onClose={tapToPayAwareness.markSeen}
                    productName={tapToPayAwareness.productName}
                  />
                ) : (
                  !isMfaStatusPending && <AnnouncementHost />
                ))}

              {showMfaSetup && (
                <MfaSetupModal
                  isOpen={showMfaSetup}
                  onClose={() => setIsMfaSetupDismissed(true)}
                  isRequired={mfaGraceExpired}
                  gracePeriodRemainingDays={mfaStatus?.gracePeriodRemainingDays}
                />
              )}

              <DashboardRoutes />

              {hasAiChatAccess && (
                <Suspense fallback={null}>
                  <AiChatPanel />
                </Suspense>
              )}
            </div>
          </div>
        </AiChatProvider>
      </HighlightProvider>
    </>
  );
};

const FiscalizationEnvironmentFloater = () => {
  const { data: posAuthData } = api.cookie.useGetPosToken();

  if (!posAuthData || posAuthData.environment === "production") return null;

  return (
    <div className="pointer-events-none fixed right-2 bottom-2 z-9999999999">
      <Chip color="danger" variant="flat" className="text-xs" size="lg">
        <span className="font-semibold">
          TAX REGISTER ENV: {posAuthData.environment.toUpperCase()}
        </span>
      </Chip>
    </div>
  );
};

export default Dashboard;
